import { CSSProperties, FC, MutableRefObject, useEffect, useRef } from 'react'

type DrawFn = (ctx: CanvasRenderingContext2D, w: number, h: number, frame: number) => void

const previewStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '16 / 9',
  display: 'block',
  background: '#080e18',
  borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
}

function usePreviewCanvas(draw: DrawFn): MutableRefObject<HTMLCanvasElement | null> {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawRef = useRef<DrawFn>(draw)
  drawRef.current = draw

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let raf = 0
    let frame = 0

    const setSize = (): void => {
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()
      canvas.width = Math.max(1, Math.round(rect.width * dpr))
      canvas.height = Math.max(1, Math.round(rect.height * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    setSize()
    const observer = new ResizeObserver(setSize)
    observer.observe(canvas)

    const loop = (): void => {
      const dpr = window.devicePixelRatio || 1
      drawRef.current(ctx, canvas.width / dpr, canvas.height / dpr, frame)
      frame++
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [])

  return canvasRef
}

// ---------- Port Defense ----------
export const PortDefensePreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    const skyGrad = ctx.createLinearGradient(0, 0, 0, h)
    skyGrad.addColorStop(0, '#0a1626')
    skyGrad.addColorStop(0.7, '#152034')
    skyGrad.addColorStop(1, '#1f2c44')
    ctx.fillStyle = skyGrad
    ctx.fillRect(0, 0, w, h)

    ctx.fillStyle = 'rgba(200, 220, 255, 0.6)'
    for (let i = 0; i < 15; i++) {
      const sx = (i * 73) % w
      const sy = (i * 41) % (h * 0.55)
      ctx.fillRect(sx, sy, 1, 1)
    }

    const seaY = h * 0.65
    ctx.fillStyle = '#0a1828'
    ctx.fillRect(0, seaY, w, h - seaY)
    for (let i = 0; i < 3; i++) {
      const wy = seaY + 4 + i * 6
      ctx.fillStyle = `rgba(58, 143, 214, ${0.18 - i * 0.05})`
      ctx.fillRect(0, wy, w, 1)
    }

    for (let i = 0; i < 4; i++) {
      const cx = 14 + i * 18
      ctx.fillStyle = '#1a1d28'
      ctx.fillRect(cx - 1, seaY - 28, 2, 28)
      ctx.fillRect(cx - 10, seaY - 28, 20, 2)
      ctx.fillRect(cx - 5, seaY - 6, 1, 6)
      ctx.fillRect(cx + 4, seaY - 6, 1, 6)
    }

    const turretX = w * 0.55
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(turretX - 7, seaY - 4, 14, 8)
    ctx.fillStyle = '#ffd166'
    ctx.fillRect(turretX - 1, seaY - 12, 2, 8)

    const cycle = frame % 90
    const vesselX = w - 18 - (cycle / 90) * (w * 0.4)
    ctx.fillStyle = '#3a4458'
    ctx.fillRect(vesselX, seaY - 6, 22, 6)
    ctx.fillRect(vesselX + 8, seaY - 11, 4, 5)
    ctx.fillStyle = '#d94a4a'
    ctx.fillRect(vesselX + 19, seaY - 8, 2, 2)

    if (cycle < 50) {
      const t = cycle / 50
      const projY = seaY - 12 - t * (seaY - 30)
      ctx.fillStyle = '#ffd166'
      ctx.fillRect(turretX - 1, projY, 2, 4)
    }
    if (cycle >= 50 && cycle < 70) {
      const eAlpha = 1 - (cycle - 50) / 20
      ctx.fillStyle = `rgba(255, 209, 102, ${eAlpha * 0.9})`
      ctx.beginPath()
      ctx.arc(vesselX + 10, seaY - 8, 6 + (cycle - 50) * 0.3, 0, Math.PI * 2)
      ctx.fill()
    }
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Reach Stacker Runner ----------
export const ReachStackerRunnerPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    const skyGrad = ctx.createLinearGradient(0, 0, 0, h)
    skyGrad.addColorStop(0, '#1a1830')
    skyGrad.addColorStop(0.6, '#3a2840')
    skyGrad.addColorStop(1, '#5a3a3a')
    ctx.fillStyle = skyGrad
    ctx.fillRect(0, 0, w, h)

    ctx.fillStyle = 'rgba(255, 200, 100, 0.4)'
    ctx.beginPath()
    ctx.arc(w * 0.78, h * 0.55, h * 0.18, 0, Math.PI * 2)
    ctx.fill()

    const groundY = h * 0.78
    for (let i = 0; i < 5; i++) {
      const cx = ((i * 50 - frame * 0.4) % (w + 60)) - 30
      ctx.fillStyle = '#1a2540'
      ctx.fillRect(cx, groundY - 16, 32, 16)
    }
    for (let i = 0; i < 3; i++) {
      const cx = 14 + i * 36
      ctx.fillStyle = '#0d1622'
      ctx.fillRect(cx - 1, groundY - 30, 2, 30)
      ctx.fillRect(cx - 12, groundY - 30, 24, 2)
    }

    ctx.fillStyle = '#152030'
    ctx.fillRect(0, groundY, w, h - groundY)
    ctx.strokeStyle = 'rgba(217, 122, 44, 0.4)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, groundY)
    ctx.lineTo(w, groundY)
    ctx.stroke()
    for (let i = 0; i < 12; i++) {
      const dx = ((i * 30 - frame * 1.5) % (w + 30)) - 15
      ctx.fillStyle = 'rgba(217, 122, 44, 0.6)'
      ctx.fillRect(dx, groundY + 4, 8, 1)
    }

    const sx = w * 0.32
    const bob = Math.sin(frame * 0.18) * 1.5
    const sy = groundY - 20 + bob
    ctx.fillStyle = 'rgba(255, 200, 100, 0.18)'
    ctx.beginPath()
    ctx.arc(sx + 10, sy + 8, 22, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#3a2c12'
    ctx.fillRect(sx, sy + 5, 24, 10)
    ctx.fillStyle = '#ffd96a'
    ctx.fillRect(sx + 1, sy + 6, 22, 8)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(sx + 13, sy, 8, 7)
    ctx.fillStyle = '#3a5a8a'
    ctx.fillRect(sx + 14, sy + 1, 6, 4)
    ctx.fillStyle = '#f0b840'
    ctx.fillRect(sx + 4, sy + 1, 12, 4)
    ctx.fillStyle = '#0d0d0d'
    ctx.beginPath()
    ctx.arc(sx + 4, sy + 16, 2.5, 0, Math.PI * 2)
    ctx.arc(sx + 20, sy + 16, 2.5, 0, Math.PI * 2)
    ctx.fill()
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Yard Haul ----------
export const YardHaulPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    ctx.fillStyle = '#0d1622'
    ctx.fillRect(0, 0, w, h)

    const cell = Math.min(w, h) / 10
    const offsetX = (w - cell * 14) / 2
    const offsetY = (h - cell * 7) / 2

    ctx.strokeStyle = 'rgba(60, 80, 110, 0.25)'
    ctx.lineWidth = 0.5
    for (let x = 0; x <= 14; x++) {
      ctx.beginPath()
      ctx.moveTo(offsetX + x * cell, offsetY)
      ctx.lineTo(offsetX + x * cell, offsetY + 7 * cell)
      ctx.stroke()
    }
    for (let y = 0; y <= 7; y++) {
      ctx.beginPath()
      ctx.moveTo(offsetX, offsetY + y * cell)
      ctx.lineTo(offsetX + 14 * cell, offsetY + y * cell)
      ctx.stroke()
    }

    const dest = { x: 11, y: 5 }
    ctx.fillStyle = 'rgba(46, 194, 126, 0.25)'
    ctx.fillRect(offsetX + dest.x * cell, offsetY + dest.y * cell, cell, cell)
    ctx.strokeStyle = '#2ec27e'
    ctx.lineWidth = 1
    ctx.strokeRect(offsetX + dest.x * cell, offsetY + dest.y * cell, cell, cell)

    const trailerLen = 5
    const tick = Math.floor(frame / 18)
    const colors = ['#ffd166', '#3a8fd6', '#d94a8f', '#2ec27e', '#d97a2c']
    for (let i = 0; i < trailerLen; i++) {
      const gx = (((tick - i) % 14) + 14) % 14
      const gy = 2 + ((tick - i) % 4)
      ctx.fillStyle = colors[i % colors.length]
      ctx.fillRect(offsetX + gx * cell + 1, offsetY + gy * cell + 1, cell - 2, cell - 2)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
      ctx.fillRect(offsetX + gx * cell + 1, offsetY + gy * cell + cell - 3, cell - 2, 2)
    }
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Yard Inspector (Pacman) ----------
export const YardPacmanPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    ctx.fillStyle = '#000'
    ctx.fillRect(0, 0, w, h)

    const cell = Math.min(w / 14, h / 8)
    const offsetX = (w - cell * 14) / 2
    const offsetY = (h - cell * 8) / 2

    const maze = [
      'XXXXXXXXXXXXXX',
      'X..........X.X',
      'X.XX.XXXX.X..X',
      'X............X',
      'X.X.XXXXXX.X.X',
      'X.X........X.X',
      'X.XXXX.XXXXXX',
      'XXXXXXXXXXXXXX',
    ]
    for (let y = 0; y < maze.length; y++) {
      for (let x = 0; x < maze[y].length; x++) {
        const c = maze[y][x]
        const px = offsetX + x * cell
        const py = offsetY + y * cell
        if (c === 'X') {
          ctx.fillStyle = '#1a4ad6'
          ctx.fillRect(px, py, cell, cell)
          ctx.strokeStyle = '#3a6df0'
          ctx.lineWidth = 0.5
          ctx.strokeRect(px + 0.5, py + 0.5, cell - 1, cell - 1)
        } else if (c === '.') {
          ctx.fillStyle = '#ffd166'
          ctx.fillRect(px + cell / 2 - 1, py + cell / 2 - 1, 2, 2)
        }
      }
    }

    const path = [
      { x: 1, y: 1 },
      { x: 2, y: 1 },
      { x: 3, y: 1 },
      { x: 4, y: 1 },
      { x: 5, y: 1 },
      { x: 6, y: 1 },
      { x: 7, y: 1 },
      { x: 8, y: 1 },
      { x: 9, y: 1 },
      { x: 10, y: 1 },
      { x: 10, y: 2 },
      { x: 10, y: 3 },
    ]
    const pIdx = Math.floor(frame / 8) % path.length
    const p = path[pIdx]
    ctx.fillStyle = '#ffd166'
    ctx.beginPath()
    const mouth = (Math.sin(frame * 0.4) + 1) * 0.4 + 0.1
    ctx.arc(
      offsetX + p.x * cell + cell / 2,
      offsetY + p.y * cell + cell / 2,
      cell * 0.42,
      mouth,
      Math.PI * 2 - mouth,
    )
    ctx.lineTo(offsetX + p.x * cell + cell / 2, offsetY + p.y * cell + cell / 2)
    ctx.fill()

    const ghosts: Array<{ color: string; x: number; y: number }> = [
      { color: '#d94a4a', x: 5, y: 5 },
      { color: '#3a8fd6', x: 8, y: 5 },
      { color: '#d94a8f', x: 11, y: 5 },
    ]
    for (const g of ghosts) {
      const gx = offsetX + g.x * cell + cell / 2
      const gy = offsetY + g.y * cell + cell / 2 + Math.sin((frame + g.x * 7) * 0.18) * 1.5
      ctx.fillStyle = g.color
      ctx.beginPath()
      ctx.arc(gx, gy, cell * 0.4, Math.PI, 0)
      ctx.lineTo(gx + cell * 0.4, gy + cell * 0.4)
      ctx.lineTo(gx - cell * 0.4, gy + cell * 0.4)
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = '#fff'
      ctx.beginPath()
      ctx.arc(gx - cell * 0.12, gy - cell * 0.05, cell * 0.1, 0, Math.PI * 2)
      ctx.arc(gx + cell * 0.12, gy - cell * 0.05, cell * 0.1, 0, Math.PI * 2)
      ctx.fill()
    }
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Vessel Invaders ----------
export const VesselInvadersPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    ctx.fillStyle = '#0d1218'
    ctx.fillRect(0, 0, w, h)

    ctx.fillStyle = 'rgba(200, 220, 255, 0.4)'
    for (let i = 0; i < 18; i++) {
      const sx = (i * 53) % w
      const sy = (i * 31) % (h * 0.6)
      ctx.fillRect(sx, sy, 1, 1)
    }

    const sway = Math.sin(frame * 0.04) * 6
    const drop = Math.floor(frame / 60) * 4

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 6; col++) {
        const ex = 20 + col * 18 + sway
        const ey = 12 + row * 14 + (drop % (h * 0.3))
        const colors = ['#d94a4a', '#d97a2c', '#ffd166']
        ctx.fillStyle = colors[row]
        ctx.beginPath()
        ctx.moveTo(ex, ey - 4)
        ctx.lineTo(ex + 5, ey + 2)
        ctx.lineTo(ex - 5, ey + 2)
        ctx.closePath()
        ctx.fill()
        ctx.fillStyle = 'rgba(0,0,0,0.35)'
        ctx.fillRect(ex - 1, ey, 2, 1)
      }
    }

    const groundY = h - 8
    ctx.fillStyle = '#3a4458'
    ctx.fillRect(0, groundY, w, 2)
    ctx.fillStyle = '#ffd166'
    for (let i = 0; i < 12; i++) {
      ctx.fillRect((i * 18) % w, groundY + 3, 6, 1)
    }

    const sx = w / 2 + Math.sin(frame * 0.05) * (w * 0.3)
    ctx.fillStyle = '#3a8fd6'
    ctx.fillRect(sx - 6, h - 16, 12, 5)
    ctx.fillStyle = '#5fb1ff'
    ctx.fillRect(sx - 1, h - 20, 2, 4)

    if (frame % 30 < 18) {
      const py = h - 22 - (frame % 30) * 1.2
      ctx.fillStyle = '#ffd166'
      ctx.fillRect(sx - 1, py, 2, 4)
    }
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Inventory Drone ----------
export const InventoryDronePreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    const sky = ctx.createLinearGradient(0, 0, 0, h)
    sky.addColorStop(0, '#0d1622')
    sky.addColorStop(0.6, '#1a2a44')
    sky.addColorStop(1, '#3a4a68')
    ctx.fillStyle = sky
    ctx.fillRect(0, 0, w, h)

    const groundY = h * 0.85
    ctx.fillStyle = '#1c2028'
    ctx.fillRect(0, groundY, w, h - groundY)

    // Hazard tape strip at the ground edge
    const hazardScroll = (frame * 1.5) % 12
    for (let x = -hazardScroll; x < w; x += 12) {
      ctx.fillStyle = '#c9a648'
      ctx.beginPath()
      ctx.moveTo(x, groundY)
      ctx.lineTo(x + 6, groundY)
      ctx.lineTo(x + 3, groundY + 4)
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = '#1a1a1a'
      ctx.beginPath()
      ctx.moveTo(x + 6, groundY)
      ctx.lineTo(x + 12, groundY)
      ctx.lineTo(x + 9, groundY + 4)
      ctx.lineTo(x + 3, groundY + 4)
      ctx.closePath()
      ctx.fill()
    }

    // Two scrolling obstacle pairs (top stack hanging from cable, bottom stack
    // sitting on the ground), with a gap between for the drone.
    const obsW = 28
    const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72', '#e6c14a']
    const pairs = [
      { phase: 0, gapTop: h * 0.42, gapBottom: h * 0.65, idx: 0 },
      { phase: 0.5, gapTop: h * 0.28, gapBottom: h * 0.55, idx: 2 },
    ]
    for (const p of pairs) {
      const cycle = (frame * 0.7) / 100 + p.phase
      const x = w * 1.1 - (cycle % 1) * (w + obsW * 2) - obsW
      // Cable
      ctx.strokeStyle = '#2a2a2a'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x + obsW * 0.3, 0)
      ctx.lineTo(x + obsW * 0.3, p.gapTop - 4)
      ctx.moveTo(x + obsW * 0.7, 0)
      ctx.lineTo(x + obsW * 0.7, p.gapTop - 4)
      ctx.stroke()
      // Top stack (hanging)
      const topH = p.gapTop - 6
      const topCount = Math.max(1, Math.floor(topH / 12))
      const topCellH = topH / topCount
      for (let i = 0; i < topCount; i++) {
        ctx.fillStyle = colors[(p.idx + i) % colors.length]
        ctx.fillRect(x, 4 + i * topCellH, obsW, topCellH)
        ctx.fillStyle = 'rgba(255, 255, 255, 0.18)'
        ctx.fillRect(x, 4 + i * topCellH, obsW, 1)
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)'
        ctx.lineWidth = 0.4
        ctx.strokeRect(x, 4 + i * topCellH, obsW, topCellH)
      }
      ctx.fillStyle = '#c9a648'
      ctx.fillRect(x - 2, p.gapTop - 4, obsW + 4, 2)
      // Bottom stack (sitting on ground)
      const botH = groundY - p.gapBottom
      const botCount = Math.max(1, Math.floor(botH / 12))
      const botCellH = botH / botCount
      for (let i = 0; i < botCount; i++) {
        const y = groundY - (i + 1) * botCellH
        ctx.fillStyle = colors[(p.idx + 3 + i) % colors.length]
        ctx.fillRect(x, y, obsW, botCellH)
        ctx.fillStyle = 'rgba(255, 255, 255, 0.18)'
        ctx.fillRect(x, y, obsW, 1)
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)'
        ctx.lineWidth = 0.4
        ctx.strokeRect(x, y, obsW, botCellH)
      }
    }

    // Drone — bobbing in the left third
    const dx = w * 0.3
    const dy = h * 0.5 + Math.sin(frame * 0.18) * 8
    const tilt = Math.sin(frame * 0.18 + 0.3) * 0.18
    const armLen = 10
    ctx.save()
    ctx.translate(dx, dy)
    ctx.rotate(tilt)
    ctx.strokeStyle = '#3a3a3a'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(-armLen, 0)
    ctx.lineTo(armLen, 0)
    ctx.stroke()
    ctx.fillStyle = '#2a3548'
    ctx.fillRect(-5, -5, 10, 9)
    ctx.fillStyle = '#ffd166'
    ctx.fillRect(-4, -4, 8, 1)
    for (const armX of [-armLen, armLen]) {
      ctx.fillStyle = 'rgba(180, 200, 230, 0.55)'
      ctx.beginPath()
      ctx.ellipse(armX, 0, 5, 1.2, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.save()
      ctx.translate(armX, 0)
      ctx.rotate(frame * 0.6)
      ctx.fillStyle = 'rgba(40, 50, 70, 0.6)'
      ctx.fillRect(-5, -0.5, 10, 1)
      ctx.restore()
    }
    // Status LED
    const led = 0.5 + Math.sin(frame * 0.25) * 0.5
    ctx.fillStyle = `rgba(80, 220, 100, ${led})`
    ctx.fillRect(-1, -7, 2, 2)
    // Camera gimbal
    ctx.fillStyle = '#1a1a1a'
    ctx.fillRect(-2, 4, 4, 2)
    ctx.fillStyle = '#0a0a0a'
    ctx.beginPath()
    ctx.arc(0, 6, 1.4, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Terminal Crossing ----------
export const TerminalCrossingPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    ctx.fillStyle = '#080e18'
    ctx.fillRect(0, 0, w, h)

    const rows = 7
    const rowH = h / rows
    const palette = [
      '#0d1f3a', // berths
      '#15253d', // harbor
      '#2a3548', // pier
      '#1d1815', // rail
      '#1a1f28', // road
      '#1a1f28', // road
      '#3a4458', // start curb
    ]
    for (let r = 0; r < rows; r++) {
      ctx.fillStyle = palette[r]
      ctx.fillRect(0, r * rowH, w, rowH)
    }

    // Berth row — 5 small ship silhouettes
    const berthY = 0
    for (let i = 0; i < 5; i++) {
      const bx = w * (0.1 + i * 0.18)
      ctx.fillStyle = i < 2 ? '#3aa15a' : '#3a4458'
      ctx.beginPath()
      ctx.moveTo(bx - 8, berthY + rowH * 0.55)
      ctx.lineTo(bx + 8, berthY + rowH * 0.55)
      ctx.lineTo(bx + 6, berthY + rowH * 0.95)
      ctx.lineTo(bx - 6, berthY + rowH * 0.95)
      ctx.closePath()
      ctx.fill()
      ctx.fillStyle = i < 2 ? '#1a3a28' : '#2a3548'
      ctx.fillRect(bx - 3, berthY + rowH * 0.3, 6, rowH * 0.25)
    }

    // Harbor row — barge / tug moving across
    const harborY = rowH
    const bargeX = ((frame * 0.6) % (w + 60)) - 30
    ctx.fillStyle = '#3a2c20'
    ctx.fillRect(bargeX, harborY + rowH * 0.55, 38, rowH * 0.25)
    const bargeColors = ['#3a8fd6', '#d97a2c', '#3aa15a']
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = bargeColors[i]
      ctx.fillRect(bargeX + 4 + i * 11, harborY + rowH * 0.2, 9, rowH * 0.4)
    }
    ctx.strokeStyle = 'rgba(120, 170, 220, 0.25)'
    ctx.lineWidth = 0.6
    for (let i = 0; i < 3; i++) {
      const yLine = harborY + rowH * (0.25 + i * 0.25)
      ctx.beginPath()
      for (let x = 0; x < w; x += 4) {
        const wave = Math.sin((frame + x) * 0.08 + i * 0.4) * 1
        if (x === 0) ctx.moveTo(x, yLine + wave)
        else ctx.lineTo(x, yLine + wave)
      }
      ctx.stroke()
    }

    // Staging pier (safe row)
    const pierY = rowH * 2
    ctx.fillStyle = 'rgba(255, 209, 102, 0.4)'
    ctx.font = 'bold 6px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.fillText('PIER', w / 2, pierY + rowH * 0.6)

    // Rail row — train moving
    const railY = rowH * 3
    ctx.fillStyle = '#3a2c20'
    for (let x = 0; x < w; x += 10) {
      ctx.fillRect(x, railY + rowH * 0.25, 6, rowH * 0.5)
    }
    ctx.strokeStyle = '#7a7a7a'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, railY + rowH * 0.35)
    ctx.lineTo(w, railY + rowH * 0.35)
    ctx.moveTo(0, railY + rowH * 0.65)
    ctx.lineTo(w, railY + rowH * 0.65)
    ctx.stroke()
    const trainX = w + 60 - ((frame * 1.1) % (w + 200))
    ctx.fillStyle = '#d94a4a'
    ctx.fillRect(trainX, railY + rowH * 0.2, 26, rowH * 0.6)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(trainX + 18, railY + rowH * 0.3, 6, rowH * 0.4)
    const trainColors = ['#3a8fd6', '#d97a2c', '#3aa15a']
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = '#3a3a3a'
      ctx.fillRect(trainX - 4 - i * 22, railY + rowH * 0.35, 20, rowH * 0.3)
      ctx.fillStyle = trainColors[i]
      ctx.fillRect(trainX - 2 - i * 22, railY + rowH * 0.2, 16, rowH * 0.4)
    }

    // Road row 1 — truck
    const road1Y = rowH * 4
    const truckX = ((frame * 0.5) % (w + 60)) - 30
    ctx.fillStyle = '#3a8fd6'
    ctx.fillRect(truckX, road1Y + rowH * 0.25, 28, rowH * 0.5)
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(truckX + 22, road1Y + rowH * 0.3, 8, rowH * 0.4)

    // Road row 2 — straddle / stacker
    const road2Y = rowH * 5
    const stackerX = w + 30 - ((frame * 0.7 + 80) % (w + 60))
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(stackerX + 4, road2Y + rowH * 0.3, 22, rowH * 0.45)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(stackerX + 8, road2Y + rowH * 0.15, 12, rowH * 0.4)
    ctx.fillStyle = '#a83a72'
    ctx.fillRect(stackerX + 24, road2Y + rowH * 0.4, 8, rowH * 0.3)
    ctx.fillStyle = '#0a0a0a'
    ctx.fillRect(stackerX + 6, road2Y + rowH * 0.78, 6, 2)
    ctx.fillRect(stackerX + 18, road2Y + rowH * 0.78, 6, 2)

    // Worker on the start curb — hops up periodically
    const startY = rowH * 6
    const hopPhase = (frame % 80) / 80
    const hopLift = -Math.sin(hopPhase * Math.PI) * 6
    const wx = w * 0.5
    const wy = startY + rowH * 0.55 + hopLift
    ctx.fillStyle = 'rgba(0,0,0,0.35)'
    ctx.beginPath()
    ctx.ellipse(wx, startY + rowH * 0.7, 6, 2, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#ffd166'
    ctx.fillRect(wx - 6, wy - 3, 12, 8)
    ctx.fillStyle = 'rgba(255,255,255,0.6)'
    ctx.fillRect(wx - 5, wy - 1, 10, 0.8)
    ctx.fillRect(wx - 5, wy + 3, 10, 0.8)
    ctx.fillStyle = '#c9a648'
    ctx.beginPath()
    ctx.arc(wx, wy - 5, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
    ctx.beginPath()
    ctx.moveTo(wx, wy - 9)
    ctx.lineTo(wx - 1.5, wy - 7)
    ctx.lineTo(wx + 1.5, wy - 7)
    ctx.closePath()
    ctx.fill()
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Donkey Stacker ----------
export const DonkeyStackerPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    const sky = ctx.createLinearGradient(0, 0, 0, h)
    sky.addColorStop(0, '#0d1622')
    sky.addColorStop(0.5, '#1a2436')
    sky.addColorStop(1, '#080e18')
    ctx.fillStyle = sky
    ctx.fillRect(0, 0, w, h)

    const platforms = 4
    const padX = w * 0.08
    const slope = h * 0.04
    const platformH = Math.max(3, h * 0.04)
    const spacing = (h * 0.78) / (platforms - 1)
    const baseY = h * 0.88

    type PlatformLine = { x1: number; y1: number; x2: number; y2: number; rollDir: number }
    const lines: PlatformLine[] = []
    for (let i = 0; i < platforms; i++) {
      const y = baseY - i * spacing
      let leftY = y
      let rightY = y
      if (i !== platforms - 1) {
        if (i % 2 === 0) {
          leftY = y - slope / 2
          rightY = y + slope / 2
        } else {
          leftY = y + slope / 2
          rightY = y - slope / 2
        }
      }
      lines.push({
        x1: padX,
        y1: leftY,
        x2: w - padX,
        y2: rightY,
        rollDir: i === platforms - 1 ? 0 : i % 2 === 0 ? 1 : -1,
      })
    }

    for (const p of lines) {
      ctx.fillStyle = '#c9a648'
      ctx.beginPath()
      ctx.moveTo(p.x1, p.y1)
      ctx.lineTo(p.x2, p.y2)
      ctx.lineTo(p.x2, p.y2 + platformH)
      ctx.lineTo(p.x1, p.y1 + platformH)
      ctx.closePath()
      ctx.fill()
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.lineWidth = 0.5
      ctx.beginPath()
      ctx.moveTo(p.x1, p.y1 + platformH)
      ctx.lineTo(p.x2, p.y2 + platformH)
      ctx.stroke()
    }

    const ladderXs = [w - padX - 18, padX + 18, w - padX - 30]
    for (let i = 0; i < platforms - 1; i++) {
      const lx = ladderXs[i % ladderXs.length]
      const lower = lines[i]
      const upper = lines[i + 1]
      const tLower = (lx - lower.x1) / (lower.x2 - lower.x1)
      const tUpper = (lx - upper.x1) / (upper.x2 - upper.x1)
      const yBottom = lower.y1 + (lower.y2 - lower.y1) * tLower
      const yTop = upper.y1 + (upper.y2 - upper.y1) * tUpper
      ctx.strokeStyle = '#c9a648'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(lx - 3, yTop)
      ctx.lineTo(lx - 3, yBottom)
      ctx.moveTo(lx + 3, yTop)
      ctx.lineTo(lx + 3, yBottom)
      ctx.stroke()
      for (let ry = yTop + 4; ry < yBottom - 1; ry += 6) {
        ctx.beginPath()
        ctx.moveTo(lx - 3, ry)
        ctx.lineTo(lx + 3, ry)
        ctx.stroke()
      }
    }

    const top = lines[platforms - 1]
    const craneX = top.x1 + 18
    ctx.fillStyle = '#c9a648'
    ctx.fillRect(craneX - 1.5, top.y1 - 12, 3, 12)
    ctx.fillRect(craneX, top.y1 - 12, 18, 2.5)
    ctx.fillStyle = '#3a2c12'
    ctx.fillRect(craneX - 5, top.y1 - 4, 10, 4)
    ctx.fillStyle = `rgba(220, 60, 60, ${0.4 + 0.5 * Math.sin(frame * 0.2)})`
    ctx.fillRect(craneX - 0.5, top.y1 - 6, 1, 1.5)

    const stackerX = top.x2 - 22
    ctx.fillStyle = '#e8b347'
    ctx.fillRect(stackerX - 6, top.y1 - 7, 12, 5)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(stackerX - 2, top.y1 - 11, 6, 4)
    ctx.fillStyle = '#5a4520'
    ctx.fillRect(stackerX - 9, top.y1 - 12, 2, 10)

    type ContainerEntity = {
      x: number
      y: number
      platformIdx: number
      colorIdx: number
    }
    const colors = ['#3a8fd6', '#d97a2c', '#3aa15a', '#a83a72', '#e6c14a', '#d94a4a']
    const containerCount = 4
    const containers: ContainerEntity[] = []
    for (let i = 0; i < containerCount; i++) {
      const platformIdx = (platforms - 2 - Math.floor((frame / 30 + i * 28) / 60)) % (platforms - 1)
      const idx = ((platformIdx % (platforms - 1)) + (platforms - 1)) % (platforms - 1)
      const p = lines[idx]
      const phase = ((frame * 0.6 + i * 80) % 220) / 220
      const t = p.rollDir > 0 ? phase : 1 - phase
      const cx = p.x1 + (p.x2 - p.x1) * t
      const cy = p.y1 + (p.y2 - p.y1) * t - platformH * 0.4
      containers.push({ x: cx, y: cy, platformIdx: idx, colorIdx: (i * 2) % colors.length })
    }
    for (const c of containers) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)'
      ctx.fillRect(c.x - 4 + 1, c.y - 3 + 1, 8, 6)
      ctx.fillStyle = colors[c.colorIdx]
      ctx.fillRect(c.x - 4, c.y - 3, 8, 6)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)'
      ctx.fillRect(c.x - 4, c.y - 3, 8, 1)
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.lineWidth = 0.5
      ctx.strokeRect(c.x - 4, c.y - 3, 8, 6)
    }

    const bottom = lines[0]
    const workerPhase = (frame * 0.5) % 100
    const workerT = workerPhase / 100
    const wx = bottom.x1 + (bottom.x2 - bottom.x1) * (0.15 + workerT * 0.3)
    const wy = bottom.y1 + (bottom.y2 - bottom.y1) * (0.15 + workerT * 0.3) - platformH * 0.4
    ctx.fillStyle = '#ffd166'
    ctx.fillRect(wx - 3, wy - 7, 6, 4)
    ctx.fillStyle = '#1a2236'
    ctx.fillRect(wx - 2.5, wy - 3, 2, 4)
    ctx.fillRect(wx + 0.5, wy - 3, 2, 4)
    ctx.fillStyle = '#d4a574'
    ctx.fillRect(wx - 2, wy - 10, 4, 3)
    ctx.fillStyle = '#ffd166'
    ctx.fillRect(wx - 3, wy - 12, 6, 2)

    const jetX = lines[1].x1 + (lines[1].x2 - lines[1].x1) * 0.55
    const jetY = lines[1].y1 + (lines[1].y2 - lines[1].y1) * 0.55
    const jetActive = frame % 90 < 50
    ctx.fillStyle = '#3a3a3a'
    ctx.fillRect(jetX - 2, jetY - 2, 4, 2)
    if (jetActive) {
      const flickerH = 14 + Math.sin(frame * 0.5) * 2
      for (let i = 0; i < flickerH; i++) {
        const a = 0.7 - (i / flickerH) * 0.5
        ctx.fillStyle =
          (i + Math.floor(frame * 0.4)) % 2 === 0
            ? `rgba(255, 240, 200, ${a})`
            : `rgba(255, 209, 102, ${a * 0.85})`
        ctx.fillRect(jetX - 2 + Math.sin(frame * 0.4 + i * 0.3) * 0.8, jetY - 2 - i, 4, 1)
      }
    }
  })
  return <canvas ref={ref} style={previewStyle} />
}

// ---------- Container Pinball ----------
export const ContainerPinballPreview: FC = () => {
  const ref = usePreviewCanvas((ctx, w, h, frame) => {
    const grad = ctx.createLinearGradient(0, 0, 0, h)
    grad.addColorStop(0, '#1a2436')
    grad.addColorStop(0.5, '#152030')
    grad.addColorStop(1, '#0d1622')
    ctx.fillStyle = grad
    ctx.fillRect(0, 0, w, h)

    const inset = 8
    ctx.strokeStyle = 'rgba(255, 209, 102, 0.5)'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(inset, h * 0.35)
    ctx.quadraticCurveTo(w / 2, -h * 0.12, w - inset, h * 0.35)
    ctx.lineTo(w - inset, h - inset)
    ctx.lineTo(inset, h - inset)
    ctx.closePath()
    ctx.stroke()

    const bumpers = [
      { x: w * 0.5, y: h * 0.32, r: h * 0.1 },
      { x: w * 0.32, y: h * 0.48, r: h * 0.08 },
      { x: w * 0.68, y: h * 0.48, r: h * 0.08 },
    ]
    for (let i = 0; i < bumpers.length; i++) {
      const b = bumpers[i]
      const flash = (frame + i * 18) % 60 < 8
      ctx.fillStyle = flash ? 'rgba(255, 209, 102, 0.4)' : 'rgba(255, 209, 102, 0.18)'
      ctx.beginPath()
      ctx.arc(b.x, b.y, b.r + 3, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = '#3a8fd6'
      ctx.beginPath()
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#ffd166'
      ctx.lineWidth = 1
      ctx.stroke()
    }

    const t = (frame % 240) / 240
    const ballX = w / 2 + Math.cos(t * Math.PI * 2) * (w * 0.28)
    const ballY = h * 0.45 + Math.sin(t * Math.PI * 4) * (h * 0.18)
    const r = Math.max(3, h * 0.045)
    ctx.fillStyle = '#3a8fd6'
    ctx.fillRect(ballX - r, ballY - r, r * 2, r * 2)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)'
    ctx.fillRect(ballX - r, ballY - r, r * 2, 1)
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
    ctx.lineWidth = 0.5
    ctx.strokeRect(ballX - r, ballY - r, r * 2, r * 2)

    const flipperY = h - 16
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#c9a648'
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.moveTo(w * 0.32, flipperY)
    ctx.lineTo(w * 0.45, flipperY + 6)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(w * 0.68, flipperY)
    ctx.lineTo(w * 0.55, flipperY + 6)
    ctx.stroke()
    ctx.lineCap = 'butt'
  })
  return <canvas ref={ref} style={previewStyle} />
}
